/**
 * Converts a number to words using Indian numbering system (lakhs, crores)
 * @param {number} num - The number to convert
 * @returns {string} - The number in words
 */
export function numberToWordsIndian(num) {
  if (num === 0) return "zero";
  if (num < 0) return "minus " + numberToWordsIndian(-num);

  const ones = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];

  const tens = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];

  function convertHundreds(n) {
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " hundred";
      n = n % 100;
      if (n > 0) result += " ";
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)];
      n = n % 10;
      if (n > 0) result += " " + ones[n];
    } else if (n > 0) {
      result += ones[n];
    }
    return result;
  }

  function convertThousands(n) {
    if (n < 1000) return convertHundreds(n);
    let result = "";
    const thousands = Math.floor(n / 1000);
    result += convertHundreds(thousands) + " thousand";
    const remainder = n % 1000;
    if (remainder > 0) {
      result += " " + convertHundreds(remainder);
    }
    return result;
  }

  function convertLakhs(n) {
    if (n < 100000) return convertThousands(n);
    let result = "";
    const lakhs = Math.floor(n / 100000);
    result += convertHundreds(lakhs) + " lakh";
    const remainder = n % 100000;
    if (remainder > 0) {
      result += " " + convertThousands(remainder);
    }
    return result;
  }

  function convertCrores(n) {
    if (n < 10000000) return convertLakhs(n);
    let result = "";
    const crores = Math.floor(n / 10000000);
    result += convertHundreds(crores) + " crore";
    const remainder = n % 10000000;
    if (remainder > 0) {
      result += " " + convertLakhs(remainder);
    }
    return result;
  }

  // Handle numbers up to 99 crores (999,999,999)
  if (num >= 100000000) {
    const hundredCrores = Math.floor(num / 100000000);
    let result = convertHundreds(hundredCrores) + " hundred";
    const remainder = num % 100000000;
    if (remainder >= 10000000) {
      result += " " + convertCrores(remainder);
    } else if (remainder > 0) {
      result += " " + convertLakhs(remainder);
    }
    return result;
  }

  return convertCrores(num);
}

function capitalizeWords(str) {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Converts a USD amount to words including cents (e.g. "One Hundred United States Dollars And Fifty Cents").
 * @param {number} amount - Dollar amount (may include decimal cents)
 * @returns {string}
 */
export function amountUsdToWords(amount) {
  const totalCents = Math.round(Math.abs(Number(amount) || 0) * 100);
  const dollars = Math.floor(totalCents / 100);
  const cents = totalCents % 100;

  const dollarWords = capitalizeWords(numberToWordsIndian(dollars));

  if (cents === 0) {
    return `${dollarWords} United States Dollars`;
  }

  const centWords = capitalizeWords(numberToWordsIndian(cents));
  if (dollars === 0) {
    return `${centWords} Cents`;
  }
  return `${dollarWords} United States Dollars And ${centWords} Cents`;
}

